/**
 * Canvas 2D fireworks
 * Motion is deltaTime-based (60fps baseline) so 60Hz / 120Hz feel the same.
 */

const FPS_BASE = 60;
const MAX_DT = 0.05; // avoid huge jumps after tab blur

// 品質プリセット（Windows / 低スペック向けに medium をデフォルト寄りにする）
const QUALITY_PRESETS = {
  high: {
    particleCount: 160,
    maxDpr: 2,
    rayCountMin: 48,
    rayCountRange: 16,
    rayGlitterMin: 10,
    rayGlitterRange: 8,
    cloudGlitterMin: 180,
    cloudGlitterRange: 60,
    rayMaxTrail: 10,
    riseTrailMax: 28,
    rayDoubleStroke: true,
    fireworkIntervalMs: 2000,
    dualRocketChance: 0.3,
  },
  medium: {
    particleCount: 100,
    maxDpr: 1.25,
    rayCountMin: 32,
    rayCountRange: 8,
    rayGlitterMin: 5,
    rayGlitterRange: 4,
    cloudGlitterMin: 90,
    cloudGlitterRange: 30,
    rayMaxTrail: 7,
    riseTrailMax: 18,
    rayDoubleStroke: false,
    fireworkIntervalMs: 2800,
    dualRocketChance: 0.15,
  },
  low: {
    particleCount: 60,
    maxDpr: 1,
    rayCountMin: 24,
    rayCountRange: 8,
    rayGlitterMin: 3,
    rayGlitterRange: 3,
    cloudGlitterMin: 50,
    cloudGlitterRange: 20,
    rayMaxTrail: 5,
    riseTrailMax: 12,
    rayDoubleStroke: false,
    fireworkIntervalMs: 3500,
    dualRocketChance: 0,
  },
};

function detectPerformanceTier() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return "low";
  }
  const cores = navigator.hardwareConcurrency || 4;
  const mem = navigator.deviceMemory || 4;
  const isWindows = /Windows/i.test(navigator.userAgent);
  if (cores <= 4 || mem <= 4) return "low";
  if (isWindows || cores <= 8) return "medium";
  return "high";
}

function resolveQuality(opts) {
  const tier = opts.qualityTier || detectPerformanceTier();
  const base = QUALITY_PRESETS[tier] || QUALITY_PRESETS.medium;
  return {
    tier,
    particleCount: opts.particleCount == null ? base.particleCount : opts.particleCount,
    maxDpr: opts.maxDpr == null ? base.maxDpr : opts.maxDpr,
    rayCountMin: base.rayCountMin,
    rayCountRange: base.rayCountRange,
    rayGlitterMin: base.rayGlitterMin,
    rayGlitterRange: base.rayGlitterRange,
    cloudGlitterMin: base.cloudGlitterMin,
    cloudGlitterRange: base.cloudGlitterRange,
    rayMaxTrail: base.rayMaxTrail,
    riseTrailMax: base.riseTrailMax,
    rayDoubleStroke: base.rayDoubleStroke,
    fireworkIntervalMs: base.fireworkIntervalMs,
    dualRocketChance: base.dualRocketChance,
  };
}

// 爆発rayのdrag定数（毎フレーム Math.log を呼ばないよう事前計算）
const RAY_DRAG_PER_SEC    = Math.log(0.985) * FPS_BASE;
const GLITTER_DRAG_PER_SEC = Math.log(0.986) * FPS_BASE;
// flash の減衰率（1フレーム = 1/60s 基準で0.86 retention）
const FLASH_DECAY_BASE = 0.86;

// 花火の色定義: colorType → 先端RGB
// colorType: "pink" = #fff→#F4B7E8, "peach" = #fff→#F3CDCD
const COLOR_TYPES = {
  pink:  { r: 244, g: 183, b: 232 },
  peach: { r: 243, g: 205, b: 205 },
};
const COLOR_TYPE_KEYS = Object.keys(COLOR_TYPES);

// 軌道色 #C9A7DF (両カラー共通)
const RISE_COLOR = "201, 167, 223";

function rand(min, max) {
  return min + Math.random() * (max - min);
}

function pickSize(minSize, maxSize) {
  if (minSize != null && maxSize != null) {
    return rand(minSize, maxSize);
  }
  const t = Math.random();
  if (t < 0.55) return rand(1.2, 4);
  if (t < 0.82) return rand(6, 22);
  return rand(28, 78);
}

function parseRgb(color) {
  if (!color || typeof color !== "string") {
    return { r: 255, g: 255, b: 255 };
  }
  const hex = color.trim();
  const longHex  = /^#([0-9a-fA-F]{6})$/;
  const shortHex = /^#([0-9a-fA-F]{3})$/;
  let match = hex.match(longHex);
  if (match) {
    return {
      r: parseInt(match[1].slice(0, 2), 16),
      g: parseInt(match[1].slice(2, 4), 16),
      b: parseInt(match[1].slice(4, 6), 16),
    };
  }
  match = hex.match(shortHex);
  if (match) {
    return {
      r: parseInt(match[1][0] + match[1][0], 16),
      g: parseInt(match[1][1] + match[1][1], 16),
      b: parseInt(match[1][2] + match[1][2], 16),
    };
  }
  return { r: 255, g: 255, b: 255 };
}

// 白(0)→先端色(1)のRGB文字列を返す
// t=0.2 付近から素早く先端色寄りになるよう補間をかける
function lerpColorStr(t, endRgb) {
  const clamped = t < 0 ? 0 : t > 1 ? 1 : t;
  const mapped = clamped < 0.2
    ? clamped / 0.2 * 0.5
    : 0.5 + (clamped - 0.2) / 0.8 * 0.5;
  const r = (255 + (endRgb.r - 255) * mapped + 0.5) | 0;
  const g = (255 + (endRgb.g - 255) * mapped + 0.5) | 0;
  const b = (255 + (endRgb.b - 255) * mapped + 0.5) | 0;
  return r + ", " + g + ", " + b;
}

function createAmbientParticle(w, h, scale, minSize, maxSize, maxAlpha) {
  const baseR = pickSize(minSize, maxSize);
  const r = baseR * scale;
  let a;
  if (maxAlpha != null) {
    a = Math.random() < 0.25 ? maxAlpha : rand(0.12, maxAlpha);
  } else if (baseR >= 28) a = rand(0.06, 0.18);
  else if (baseR >= 6) a = rand(0.12, 0.35);
  else a = rand(0.35, 0.85);

  return {
    x: Math.random() * w,
    y: Math.random() * h,
    r,
    vx: rand(-0.1, 0.1) * FPS_BASE,
    vy: rand(-0.14, 0.03) * FPS_BASE,
    a,
    pulse: rand(0, Math.PI * 2),
    pulseSpeed: rand(0.004, 0.014) * FPS_BASE,
  };
}

function createRocket(w, h, burst, colorType) {
  const scale = Math.min(w, h) / 900;
  const ct = COLOR_TYPES[colorType] ? colorType : "pink";
  return {
    phase: "rise",
    x: burst.x,
    y: h + 12,
    targetY: burst.targetY,
    colorType: ct,
    endRgb: COLOR_TYPES[ct],
    vy: rand(-8.5, -6.5) * FPS_BASE * scale,
    wobble: 0.35 * scale * FPS_BASE,
    trail: [],
    rays: [],
    glitter: [],
    flash: 0,
  };
}

function explode(fw, w, h, quality) {
  const scale = Math.min(w, h) / 900;
  const rayCount = quality.rayCountMin + Math.floor(rand(0, quality.rayCountRange));
  const rays = [];
  const glitter = [];
  const gravity = 0.085 * scale * FPS_BASE * FPS_BASE;
  const endRgb = fw.endRgb;
  const fx = fw.x;
  const fy = fw.y;

  for (let i = 0; i < rayCount; i += 1) {
    const angle = (Math.PI * 2 * i) / rayCount + rand(-0.06, 0.06);
    const speed = rand(4.2, 8.5) * scale * FPS_BASE;
    const cosA = Math.cos(angle);
    const sinA = Math.sin(angle);
    rays.push({
      x: fx,
      y: fy,
      vx: cosA * speed,
      vy: sinA * speed,
      gravity,
      dragPerSecond: RAY_DRAG_PER_SEC,
      life: 1,
      decay: rand(0.022, 0.036) * FPS_BASE,
      tipWidth: rand(2.8, 4.6),
      trail: [{ x: fx, y: fy }],
      maxTrail: quality.rayMaxTrail,
    });

    const along = quality.rayGlitterMin + Math.floor(rand(0, quality.rayGlitterRange));
    for (let j = 0; j < along; j += 1) {
      const t = rand(0.15, 1);
      const s = speed * t * rand(0.35, 0.85);
      // 色は生成時に焼き付ける（描画時に毎回計算しない）
      glitter.push({
        x: fx,
        y: fy,
        vx: cosA * s + rand(-0.35, 0.35) * FPS_BASE,
        vy: sinA * s + rand(-0.35, 0.35) * FPS_BASE,
        gravity: gravity * 0.9,
        dragPerSecond: GLITTER_DRAG_PER_SEC,
        life: rand(0.55, 0.9),
        decay: rand(0.02, 0.035) * FPS_BASE,
        r: rand(0.55, 1.7),
        delay: rand(0, 0.08),
        rgb: lerpColorStr(t * 0.6 + 0.3, endRgb), // 散らばり具合に応じた色
      });
    }
  }

  const cloud = quality.cloudGlitterMin + Math.floor(rand(0, quality.cloudGlitterRange));
  for (let i = 0; i < cloud; i += 1) {
    const angle = rand(0, Math.PI * 2);
    const speed = rand(0.8, 5.5) * scale * FPS_BASE;
    const t = speed / (5.5 * scale * FPS_BASE); // 速さに応じて外側ほど先端色
    glitter.push({
      x: fx,
      y: fy,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      gravity: gravity * 0.85,
      dragPerSecond: GLITTER_DRAG_PER_SEC,
      life: rand(0.5, 0.85),
      decay: rand(0.022, 0.038) * FPS_BASE,
      r: rand(0.45, 1.5),
      delay: rand(0, 0.1),
      rgb: lerpColorStr(t, endRgb),
    });
  }

  fw.phase = "burst";
  fw.rays = rays;
  fw.glitter = glitter;
  fw.flash = 1;
}

function createCanvas2DScene(canvas, options) {
  const opts = options || {};
  const quality = resolveQuality(opts);
  const particleScale = opts.particleScale == null ? 1 : opts.particleScale;
  const particleMinSize = opts.particleMinSize;
  const particleMaxSize = opts.particleMaxSize;
  const particleMaxAlpha = opts.particleMaxAlpha;
  const enableFireworks = opts.fireworks !== false;
  const particleCount = opts.particleCount == null ? quality.particleCount : opts.particleCount;
  const particleRgb = parseRgb(opts.particleColor);
  // particleRgbStr: 毎フレームの文字列生成を省く
  const particleRgbStr = particleRgb.r + ", " + particleRgb.g + ", " + particleRgb.b;
  const avoidSelector = opts.avoidSelector || null;
  const avoidPadding = opts.avoidPadding == null ? 40 : opts.avoidPadding;
  const minGutter = 24;
  const pinToViewportEnabled = !!opts.pinToViewport;

  const ctx = canvas.getContext("2d", { alpha: true });
  let w = 0;
  let h = 0;
  let dpr = 1;
  let ambient = [];
  let fireworks = [];
  let lastFire = 0;
  let lastNow = 0;
  let raf = 0;
  let running = false;
  let resizeObserver = null;
  let intersectionObserver = null;
  let inView = true;
  let pageVisible = !document.hidden;
  let pinY = -1;

  function onVisibilityChange() {
    pageVisible = !document.hidden;
    if (pageVisible) lastNow = 0;
  }

  function getLargeViewportHeight() {
    const inner = window.innerHeight || 0;
    const visual = window.visualViewport ? Math.round(window.visualViewport.height) : 0;
    const client = document.documentElement.clientHeight || 0;
    return Math.max(inner, visual, client) || 1;
  }

  function pinToViewport() {
    if (!pinToViewportEnabled) return;
    const parent = canvas.parentElement;
    if (!parent) return;
    const parentH = parent.offsetHeight || 0;
    const canvasH = canvas.offsetHeight || h || 1;
    const parentTop = parent.getBoundingClientRect().top;
    let y = parentTop < 0 ? -parentTop : 0;
    const maxY = Math.max(0, parentH - canvasH);
    if (y > maxY) y = maxY;
    y = Math.round(y);
    if (y === pinY) return;
    pinY = y;
    canvas.style.transform = "translate3d(0," + y + "px,0)";
  }

  function resize() {
    const parent = canvas.parentElement;
    const rect = canvas.getBoundingClientRect();
    dpr = Math.min(window.devicePixelRatio || 1, quality.maxDpr);
    const viewH = getLargeViewportHeight();
    const parentH = parent ? parent.offsetHeight : 0;
    const nextW = Math.max(
      1,
      Math.floor(rect.width || (parent && parent.clientWidth) || window.innerWidth)
    );
    let nextH = Math.floor(rect.height);
    if (pinToViewportEnabled) {
      // 親全体の高さ／未計測のときだけビューポート高さに抑える。
      // innerHeight で上書きしない（検索窓の出し入れで縮み、下部が空くため）
      if (nextH < 1 || (parentH > 0 && nextH >= parentH - 1)) {
        nextH = viewH;
      }
    } else if (nextH < 1) {
      nextH = Math.floor((parent && parent.clientHeight) || viewH);
    }
    nextH = Math.max(1, nextH);
    if (nextW === w && nextH === h && ambient.length) {
      pinToViewport();
      return;
    }
    w = nextW;
    h = nextH;
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ambient = Array.from({ length: particleCount }, () =>
      createAmbientParticle(w, h, particleScale, particleMinSize, particleMaxSize, particleMaxAlpha)
    );
    pinY = -1;
    pinToViewport();
  }

  function getAvoidRect() {
    if (!avoidSelector) return null;
    const el =
      typeof avoidSelector === "string"
        ? document.querySelector(avoidSelector)
        : avoidSelector;
    if (!el) return null;
    const canvasRect = canvas.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    return {
      left:   r.left   - canvasRect.left - avoidPadding,
      right:  r.right  - canvasRect.left + avoidPadding,
      top:    r.top    - canvasRect.top  - avoidPadding,
      bottom: r.bottom - canvasRect.top  + avoidPadding,
    };
  }

  function pickBurstPoint() {
    const yMin = h * 0.18;
    const yMax = h * 0.45;
    const xMin = w * 0.06;
    const xMax = w * 0.94;
    const avoid = getAvoidRect();

    if (!avoid) {
      return { x: rand(w * 0.2, w * 0.8), targetY: rand(yMin, yMax) };
    }

    const regions = [];
    const leftMax = Math.min(xMax, avoid.left);
    if (leftMax - xMin >= minGutter) {
      regions.push({ x0: xMin, x1: leftMax, y0: yMin, y1: yMax });
    }
    const rightMin = Math.max(xMin, avoid.right);
    if (xMax - rightMin >= minGutter) {
      regions.push({ x0: rightMin, x1: xMax, y0: yMin, y1: yMax });
    }

    if (!regions.length) {
      const aboveBottom = Math.min(yMax, avoid.top);
      if (aboveBottom - yMin >= minGutter) {
        regions.push({ x0: xMin, x1: xMax, y0: yMin, y1: aboveBottom });
      }
      const belowTop = Math.max(yMin, avoid.bottom);
      if (yMax - belowTop >= minGutter) {
        regions.push({ x0: xMin, x1: xMax, y0: belowTop, y1: yMax });
      }
    }

    if (!regions.length) return null;

    const region = regions[Math.floor(Math.random() * regions.length)];
    return {
      x: rand(region.x0, region.x1),
      targetY: rand(region.y0, region.y1),
    };
  }

  function nudgeOutsideAvoid(fw) {
    const avoid = getAvoidRect();
    if (!avoid) return;
    const insideX = fw.x > avoid.left && fw.x < avoid.right;
    const insideY = fw.y > avoid.top  && fw.y < avoid.bottom;
    if (!(insideX && insideY)) return;

    const dLeft   = fw.x - avoid.left;
    const dRight  = avoid.right  - fw.x;
    const dTop    = fw.y - avoid.top;
    const dBottom = avoid.bottom - fw.y;
    const nearest = Math.min(dLeft, dRight, dTop, dBottom);
    if      (nearest === dLeft)   fw.x = avoid.left;
    else if (nearest === dRight)  fw.x = avoid.right;
    else if (nearest === dTop)    fw.y = avoid.top;
    else                          fw.y = avoid.bottom;
  }

  let rocketColorIndex = 0;

  function spawnRocket() {
    const burst = pickBurstPoint();
    if (!burst) return;
    const colorType = COLOR_TYPE_KEYS[rocketColorIndex % COLOR_TYPE_KEYS.length];
    rocketColorIndex += 1;
    fireworks.push(createRocket(w, h, burst, colorType));
  }

  function updateAmbient(dt) {
    for (const p of ambient) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.pulse += p.pulseSpeed * dt;
      const pad = p.r + 2;
      if (p.x < -pad) p.x = w + pad;
      if (p.x > w + pad) p.x = -pad;
      if (p.y < -pad) p.y = h + pad;
      if (p.y > h + pad) p.y = -pad;
    }
  }

  function drawAmbient() {
    for (const p of ambient) {
      const alpha = p.a >= 1 ? 1 : p.a * (0.85 + 0.15 * Math.sin(p.pulse));
      ctx.beginPath();
      ctx.fillStyle = `rgba(${particleRgbStr}, ${alpha})`;
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  function updateFireworks(now, dt) {
    if (!enableFireworks) return;

    if (now - lastFire > quality.fireworkIntervalMs + rand(-400, 600)) {
      spawnRocket();
      if (Math.random() < quality.dualRocketChance) {
        setTimeout(() => {
          if (running) spawnRocket();
        }, rand(220, 480));
      }
      lastFire = now;
    }

    fireworks = fireworks.filter((fw) => {
      if (fw.phase === "rise") {
        fw.trail.push({ x: fw.x, y: fw.y });
        if (fw.trail.length > quality.riseTrailMax) fw.trail.shift();
        fw.y += fw.vy * dt;
        fw.x += Math.sin(fw.y * 0.018) * fw.wobble * dt;
        if (fw.y <= fw.targetY) {
          nudgeOutsideAvoid(fw);
          explode(fw, w, h, quality);
        }
        return true;
      }

      if (fw.flash > 0) {
        fw.flash *= Math.pow(FLASH_DECAY_BASE, FPS_BASE * dt);
      }

      fw.rays = fw.rays.filter((ray) => {
        const drag = Math.exp(ray.dragPerSecond * dt);
        ray.vx *= drag;
        ray.vy = ray.vy * drag + ray.gravity * dt;
        ray.x += ray.vx * dt;
        ray.y += ray.vy * dt;
        ray.trail.push({ x: ray.x, y: ray.y });
        if (ray.trail.length > ray.maxTrail) ray.trail.shift();
        ray.life -= ray.decay * dt;
        return ray.life > 0.04;
      });

      fw.glitter = fw.glitter.filter((g) => {
        if (g.delay > 0) {
          g.delay -= dt;
          return true;
        }
        const drag = Math.exp(g.dragPerSecond * dt);
        g.vx *= drag;
        g.vy = g.vy * drag + g.gravity * dt;
        g.x += g.vx * dt;
        g.y += g.vy * dt;
        g.life -= g.decay * dt;
        return g.life > 0;
      });

      return fw.rays.length > 0 || fw.glitter.length > 0;
    });
  }

  function drawRise(fw) {
    const len = fw.trail.length;
    for (let i = 0; i < len; i += 1) {
      const pt = fw.trail[i];
      const a = (i / len) * 0.5;
      if (i % 2 !== 0 && a < 0.2) continue;
      ctx.beginPath();
      ctx.fillStyle = `rgba(${RISE_COLOR}, ${a})`;
      ctx.arc(pt.x, pt.y, i > len - 4 ? 1.6 : 1, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.beginPath();
    ctx.fillStyle = `rgba(${RISE_COLOR}, 0.95)`;
    ctx.arc(fw.x, fw.y, 2.2, 0, Math.PI * 2);
    ctx.fill();
  }

  function drawBurst(fw) {
    ctx.save();
    ctx.globalCompositeOperation = "lighter";

    if (fw.flash > 0.04) {
      ctx.beginPath();
      ctx.fillStyle = `rgba(255, 255, 255, ${fw.flash * 0.5})`;
      ctx.arc(fw.x, fw.y, 8 + fw.flash * 14, 0, Math.PI * 2);
      ctx.fill();
    }

    const endRgb = fw.endRgb;
    // tipRgb はray共通で1回だけ計算
    const tipRgb = endRgb.r + ", " + endRgb.g + ", " + endRgb.b;

    for (const ray of fw.rays) {
      const trail = ray.trail;
      if (trail.length < 2) continue;
      const life = Math.max(0, ray.life);
      ctx.lineCap = "round";

      for (let i = 1; i < trail.length; i += 1) {
        const t = i / (trail.length - 1);
        const p0 = trail[i - 1];
        const p1 = trail[i];
        const width = 0.4 + ray.tipWidth * Math.pow(t, 1.6);
        const alpha = life * (0.25 + 0.75 * t);
        const rgb = lerpColorStr(t, endRgb);

        if (quality.rayDoubleStroke) {
          ctx.lineWidth = width * 1.8;
          ctx.strokeStyle = `rgba(${rgb}, ${alpha * 0.35})`;
          ctx.beginPath();
          ctx.moveTo(p0.x, p0.y);
          ctx.lineTo(p1.x, p1.y);
          ctx.stroke();
        }

        ctx.lineWidth = width;
        ctx.strokeStyle = `rgba(${rgb}, ${alpha})`;
        ctx.beginPath();
        ctx.moveTo(p0.x, p0.y);
        ctx.lineTo(p1.x, p1.y);
        ctx.stroke();
      }

      const tip = trail[trail.length - 1];
      ctx.beginPath();
      ctx.fillStyle = `rgba(${tipRgb}, ${life})`;
      ctx.arc(tip.x, tip.y, ray.tipWidth * 0.55, 0, Math.PI * 2);
      ctx.fill();
    }

    for (const g of fw.glitter) {
      if (g.delay > 0) continue;
      const a = Math.max(0, g.life);
      ctx.beginPath();
      ctx.fillStyle = `rgba(${g.rgb}, ${a})`;
      ctx.arc(g.x, g.y, g.r, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  function drawFireworks() {
    for (const fw of fireworks) {
      if (fw.phase === "rise") drawRise(fw);
      else drawBurst(fw);
    }
  }

  function frame(now) {
    if (!running) return;
    pinToViewport();

    // 画面外・タブ非表示時は更新を止める
    if (!inView || !pageVisible) {
      lastNow = 0;
      raf = requestAnimationFrame(frame);
      return;
    }

    if (!lastNow) lastNow = now;
    let dt = (now - lastNow) / 1000;
    lastNow = now;
    if (dt < 0) dt = 0;
    if (dt > MAX_DT) dt = MAX_DT;

    ctx.clearRect(0, 0, w, h);
    updateAmbient(dt);
    drawAmbient();
    if (enableFireworks) {
      updateFireworks(now, dt);
      drawFireworks();
    }
    raf = requestAnimationFrame(frame);
  }

  return {
    start() {
      if (running) return;
      running = true;
      resize();
      // レイアウト確定後に再計測（高さ0で起動するのを防ぐ）
      requestAnimationFrame(resize);
      lastFire = performance.now() - 1000;
      lastNow = 0;
      raf = requestAnimationFrame(frame);
      window.addEventListener("resize", resize);
      if (pinToViewportEnabled) {
        window.addEventListener("scroll", pinToViewport, { passive: true });
        if (window.visualViewport) {
          window.visualViewport.addEventListener("resize", resize);
          window.visualViewport.addEventListener("scroll", pinToViewport, { passive: true });
        }
      }
      document.addEventListener("visibilitychange", onVisibilityChange);
      if (typeof ResizeObserver === "function") {
        resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe(canvas.parentElement || canvas);
      }
      if (typeof IntersectionObserver === "function") {
        intersectionObserver = new IntersectionObserver(
          (entries) => {
            inView = entries[0] ? entries[0].isIntersecting : true;
          },
          { root: null, rootMargin: "80px", threshold: 0 }
        );
        intersectionObserver.observe(canvas);
      }
    },
    stop() {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      if (pinToViewportEnabled) {
        window.removeEventListener("scroll", pinToViewport);
        if (window.visualViewport) {
          window.visualViewport.removeEventListener("resize", resize);
          window.visualViewport.removeEventListener("scroll", pinToViewport);
        }
      }
      document.removeEventListener("visibilitychange", onVisibilityChange);
      pinY = -1;
      canvas.style.transform = "";
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
      if (intersectionObserver) {
        intersectionObserver.disconnect();
        intersectionObserver = null;
      }
    },
    resize,
  };
}

window.createCanvas2DScene = createCanvas2DScene;
